/**
 * Rhythmz — Scene: Geometric Bloom
 * ──────────────────────────────────
 * 200 faceted icosahedra arranged on a sphere surface. Explodes outward on
 * beat, reassembles into a sphere. High metalness + 3-point lighting so each
 * facet catches reflections like a glitter bomb.
 *
 * Audio: Beat → explosion + reassemble. Bass → size pulse + rotation speed.
 */

import * as THREE from 'three/webgpu';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface BloomUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

const BLOOM_COUNT = 200;

export function createGeometricBloom(u: BloomUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(0.9, 0.6, 0.3), keyIntensity: 18, keyPosition: new THREE.Vector3(3, 5, 4),
    fillColor: new THREE.Color(0.2, 0.4, 0.9), fillIntensity: 6, fillPosition: new THREE.Vector3(-3, -1, 3),
    rimColor: new THREE.Color(0.1, 0.9, 0.5), rimIntensity: 8, rimPosition: new THREE.Vector3(0, -3, -5),
    camLightIntensity: 5,
  });
  group.add(lighting.group);

  const facetMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 1.0, roughness: 0.05, clearcoat: 1.0, color: 0x8844aa, emissive: 0xff66aa, emissiveIntensity: 3 });
  const facetGeo = new THREE.IcosahedronGeometry(0.08, 2);

  const facets = new THREE.InstancedMesh(facetGeo, facetMat, BLOOM_COUNT);
  facets.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(facets);

  // Fibonacci sphere base positions
  const golden = Math.PI * (3 - Math.sqrt(5));
  const basePos: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < BLOOM_COUNT; i++) {
    const y = 1 - (i / (BLOOM_COUNT - 1)) * 2;
    const rad = Math.sqrt(1 - y * y);
    const theta = golden * i;
    basePos.push({ x: Math.cos(theta) * rad, y, z: Math.sin(theta) * rad });
  }

  // Each facet's "explode" direction (same as its sphere position)
  let isExploded = false;
  let explodeTime = 0;
  const EXPLODE_DURATION = 1.0;
  const restPositions = [...basePos];
  const explodePositions = basePos.map(p => ({
    x: p.x * (2 + Math.random() * 3),
    y: p.y * (2 + Math.random() * 3),
    z: p.z * (2 + Math.random() * 3),
  }));

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = { bass: u.uBass.value ?? 0, beat: u.uBeat.value ?? 0, mid: u.uMid.value ?? 0, level: ((u.uBass.value ?? 0)+(u.uMid.value ?? 0))*0.5 };
    lighting.update(audio);
    const beat = u.uBeat.value ?? 0;
    const bass = u.uBass.value ?? 0;

    // Trigger explode on new beat
    if (beat > 0.3 && !isExploded) {
      isExploded = true;
      explodeTime = 0;
    }
    if (isExploded) {
      explodeTime += dt;
      if (explodeTime > EXPLODE_DURATION) {
        isExploded = false;
      }
    }

    for (let i = 0; i < BLOOM_COUNT; i++) {
      const base = restPositions[i];
      const expl = explodePositions[i];
      let px: number, py: number, pz: number;

      if (isExploded) {
        const progress = explodeTime / EXPLODE_DURATION;
        // Ease out: fast explosion, slow return
        const easeOut = 1 - Math.pow(1 - progress, 2);
        px = base.x + (expl.x - base.x) * easeOut;
        py = base.y + (expl.y - base.y) * easeOut;
        pz = base.z + (expl.z - base.z) * easeOut;
      } else {
        px = base.x; py = base.y; pz = base.z;
      }

      // Bass pulse: all facets pulse
      const pulse = 1 + bass * 0.4;
      const r = Math.sqrt(px * px + py * py + pz * pz);
      const orbPulse = r * (1 + bass * 0.15 * Math.sin(t * 2 + i));
      const normPx = px / r, normPy = py / r, normPz = pz / r;
      pivot.position.set(normPx * orbPulse * pulse, normPy * orbPulse * pulse, normPz * orbPulse * pulse);
      pivot.rotation.set(t * (0.3 + bass) + i, t * (0.5 + bass) + i * 0.7, 0);
      const scale = 1 + bass * 0.3 + beat * 0.5;
      pivot.scale.setScalar(scale * (0.8 + Math.sin(t * 2 + i * 0.5) * 0.2));
      pivot.updateMatrix();
      facets.setMatrixAt(i, pivot.matrix);
    }
    facets.instanceMatrix.needsUpdate = true;
    group.rotation.y = t * 0.1;
  };

  const dispose = (): void => {
    lighting.dispose();
    facetGeo.dispose(); facetMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Geometric Bloom' };
}